/**
 * Per-Agent DEV Test Overrides
 * 
 * DEV/INTERNAL USE ONLY
 * 
 * Provides session-scoped, per-agent eligibility overrides for testing
 * routing scenarios when no eligible agents exist.
 * 
 * These overrides:
 * - Are session-scoped and reset on page reload
 * - Never persist to database
 * - Never affect simulations
 * - Only apply in live routing + retries
 * - Are clearly labeled in routing attempts
 */

import { isDevMode } from '@/lib/featureFlags';
import type { MockAgent } from '@/contexts/MockAgentsContext';

// --- Per-Agent Override Configuration ---

export interface PerAgentOverrides {
  /** Force this agent to accept all lead types (bypass leadPreferences.isActive check) */
  forceAcceptAllLeadTypes: boolean;
  /** Override accepted lead types (add these as active) */
  overrideAcceptedLeadTypes: string[];
  /** Override licensed states (add these to licensedStates) */
  overrideLicensedStates: string[];
  /** Ignore contract caps (daily/monthly) */
  ignoreContractCaps: boolean;
  /** Ignore pricing rules (allow any bid) */
  ignorePricingRules: boolean;
}

export const DEFAULT_PER_AGENT_OVERRIDES: PerAgentOverrides = {
  forceAcceptAllLeadTypes: false,
  overrideAcceptedLeadTypes: [],
  overrideLicensedStates: [],
  ignoreContractCaps: false,
  ignorePricingRules: false,
};

// --- Session Store ---

/**
 * Session-scoped store of per-agent overrides.
 * Key: agentId, Value: PerAgentOverrides
 */
const PER_AGENT_OVERRIDE_STORE: Map<string, PerAgentOverrides> = new Map();

/**
 * Global enable flag for per-agent overrides.
 * Must be true for any per-agent overrides to apply.
 */
let perAgentOverridesEnabled = false;

// --- Public API ---

/**
 * Enable/disable per-agent overrides globally.
 */
export function setPerAgentOverridesEnabled(enabled: boolean): void {
  if (!isDevMode) {
    console.warn('[Per-Agent Overrides] Cannot enable outside development mode');
    return;
  }
  perAgentOverridesEnabled = enabled;
  console.log('[Per-Agent Overrides] Enabled:', enabled);
}

/**
 * Check if per-agent overrides are active.
 */
export function arePerAgentOverridesActive(): boolean {
  return isDevMode && perAgentOverridesEnabled && PER_AGENT_OVERRIDE_STORE.size > 0;
}

/**
 * Check if per-agent overrides are enabled (even if no agents have overrides).
 */
export function arePerAgentOverridesEnabled(): boolean {
  return isDevMode && perAgentOverridesEnabled;
}

/**
 * Get the override configuration for a specific agent.
 * Returns undefined if no overrides are set.
 */
export function getAgentOverrides(agentId: string): PerAgentOverrides | undefined {
  if (!perAgentOverridesEnabled) return undefined;
  return PER_AGENT_OVERRIDE_STORE.get(agentId);
}

/**
 * Get all agent IDs that have active overrides.
 */
export function getAgentsWithOverrides(): string[] {
  return Array.from(PER_AGENT_OVERRIDE_STORE.keys());
}

/**
 * Get all per-agent overrides as a map.
 */
export function getAllPerAgentOverrides(): Map<string, PerAgentOverrides> {
  return new Map(PER_AGENT_OVERRIDE_STORE);
}

/**
 * Set overrides for a specific agent.
 */
export function setAgentOverrides(agentId: string, overrides: Partial<PerAgentOverrides>): void {
  if (!isDevMode) {
    console.warn('[Per-Agent Overrides] Cannot set overrides outside development mode');
    return;
  }
  
  const existing = PER_AGENT_OVERRIDE_STORE.get(agentId) ?? { ...DEFAULT_PER_AGENT_OVERRIDES };
  const updated = { ...existing, ...overrides };
  
  // Check if any override is actually set
  const hasAnyOverride = 
    updated.forceAcceptAllLeadTypes ||
    updated.overrideAcceptedLeadTypes.length > 0 ||
    updated.overrideLicensedStates.length > 0 ||
    updated.ignoreContractCaps ||
    updated.ignorePricingRules;
  
  if (hasAnyOverride) {
    PER_AGENT_OVERRIDE_STORE.set(agentId, updated);
    console.log(`[Per-Agent Overrides] Updated agent ${agentId}:`, updated);
  } else {
    // Remove if all overrides are cleared
    PER_AGENT_OVERRIDE_STORE.delete(agentId);
    console.log(`[Per-Agent Overrides] Cleared overrides for agent ${agentId}`);
  }
}

/**
 * Clear overrides for a specific agent.
 */
export function clearAgentOverrides(agentId: string): void {
  PER_AGENT_OVERRIDE_STORE.delete(agentId);
  console.log(`[Per-Agent Overrides] Cleared for agent ${agentId}`);
}

/**
 * Reset all per-agent overrides.
 */
export function resetAllPerAgentOverrides(): void {
  PER_AGENT_OVERRIDE_STORE.clear();
  perAgentOverridesEnabled = false;
  console.log('[Per-Agent Overrides] Reset all');
}

// --- Agent Transformation ---

/**
 * Apply per-agent overrides to a single agent.
 * Returns a modified copy (does not mutate original).
 */
export function applyPerAgentOverrides(agent: MockAgent): MockAgent {
  if (!perAgentOverridesEnabled) return agent;
  
  const overrides = PER_AGENT_OVERRIDE_STORE.get(agent.id);
  if (!overrides) return agent;
  
  const modified = { ...agent };
  
  // Apply lead type overrides
  if (overrides.forceAcceptAllLeadTypes && modified.leadPreferences) {
    modified.leadPreferences = modified.leadPreferences.map(pref => ({
      ...pref,
      isActive: true,
      // Set a reasonable default bid if not set
      bidValue: pref.bidValue > 0 ? pref.bidValue : 50,
    }));
  } else if (overrides.overrideAcceptedLeadTypes.length > 0 && modified.leadPreferences) {
    modified.leadPreferences = modified.leadPreferences.map(pref => ({
      ...pref,
      isActive: pref.isActive || overrides.overrideAcceptedLeadTypes.includes(pref.leadTypeId),
      bidValue: pref.bidValue > 0 ? pref.bidValue : 50,
    }));
  }
  
  // Apply licensed states overrides
  if (overrides.overrideLicensedStates.length > 0) {
    const existingStates = new Set(modified.licensedStates);
    for (const state of overrides.overrideLicensedStates) {
      existingStates.add(state);
    }
    modified.licensedStates = Array.from(existingStates);
  }
  
  // Contract caps and pricing rules are handled during eligibility checking
  // We attach the override info to the agent for the eligibility check to consume
  
  return modified;
}

/**
 * Apply per-agent overrides to a pool of agents.
 * Returns a new array with modified copies.
 */
export function applyPerAgentOverridesToPool(agents: MockAgent[]): MockAgent[] {
  if (!perAgentOverridesEnabled || PER_AGENT_OVERRIDE_STORE.size === 0) {
    return agents;
  }
  
  const overriddenAgentIds = Array.from(PER_AGENT_OVERRIDE_STORE.keys());
  console.log(`[Per-Agent Overrides] Applying to ${overriddenAgentIds.length} agent(s)`);
  
  return agents.map(agent => applyPerAgentOverrides(agent));
}

// --- Eligibility Context Extension ---

/**
 * Check if an agent should ignore contract caps.
 */
export function shouldIgnoreContractCaps(agentId: string): boolean {
  if (!perAgentOverridesEnabled) return false;
  const overrides = PER_AGENT_OVERRIDE_STORE.get(agentId);
  return overrides?.ignoreContractCaps ?? false;
}

/**
 * Check if an agent should ignore pricing rules.
 */
export function shouldIgnorePricingRules(agentId: string): boolean {
  if (!perAgentOverridesEnabled) return false;
  const overrides = PER_AGENT_OVERRIDE_STORE.get(agentId);
  return overrides?.ignorePricingRules ?? false;
}

// --- Routing Context Capture ---

/**
 * Capture per-agent overrides for persistence in routing context.
 */
export interface PerAgentOverridesSnapshot {
  enabled: boolean;
  agentOverrides: Record<string, PerAgentOverrides>;
  capturedAt: number;
}

export function capturePerAgentOverridesSnapshot(): PerAgentOverridesSnapshot | undefined {
  if (!perAgentOverridesEnabled || PER_AGENT_OVERRIDE_STORE.size === 0) {
    return undefined;
  }
  
  const agentOverrides: Record<string, PerAgentOverrides> = {};
  for (const [agentId, overrides] of PER_AGENT_OVERRIDE_STORE) {
    agentOverrides[agentId] = { ...overrides };
  }
  
  return {
    enabled: true,
    agentOverrides,
    capturedAt: Date.now(),
  };
}

/**
 * Apply a captured snapshot for routing context (used in retries).
 */
export function applyPerAgentOverridesFromSnapshot(
  agents: MockAgent[],
  snapshot: PerAgentOverridesSnapshot
): MockAgent[] {
  if (!snapshot.enabled || Object.keys(snapshot.agentOverrides).length === 0) {
    return agents;
  }
  
  console.log('[Per-Agent Overrides] Applying from snapshot');
  
  return agents.map(agent => {
    const overrides = snapshot.agentOverrides[agent.id];
    if (!overrides) return agent;
    
    const modified = { ...agent };
    
    // Apply lead type overrides
    if (overrides.forceAcceptAllLeadTypes && modified.leadPreferences) {
      modified.leadPreferences = modified.leadPreferences.map(pref => ({
        ...pref,
        isActive: true,
        bidValue: pref.bidValue > 0 ? pref.bidValue : 50,
      }));
    } else if (overrides.overrideAcceptedLeadTypes.length > 0 && modified.leadPreferences) {
      modified.leadPreferences = modified.leadPreferences.map(pref => ({
        ...pref,
        isActive: pref.isActive || overrides.overrideAcceptedLeadTypes.includes(pref.leadTypeId),
        bidValue: pref.bidValue > 0 ? pref.bidValue : 50,
      }));
    }
    
    // Apply licensed states overrides
    if (overrides.overrideLicensedStates.length > 0) {
      const existingStates = new Set(modified.licensedStates);
      for (const state of overrides.overrideLicensedStates) {
        existingStates.add(state);
      }
      modified.licensedStates = Array.from(existingStates);
    }
    
    return modified;
  });
}

// --- Debug/Summary ---

/**
 * Get a summary of active per-agent overrides for display.
 */
export function getPerAgentOverridesSummary(): { agentId: string; summary: string[] }[] {
  if (!perAgentOverridesEnabled || PER_AGENT_OVERRIDE_STORE.size === 0) {
    return [];
  }
  
  const result: { agentId: string; summary: string[] }[] = [];
  
  for (const [agentId, overrides] of PER_AGENT_OVERRIDE_STORE) {
    const summary: string[] = [];
    
    if (overrides.forceAcceptAllLeadTypes) {
      summary.push('Accept All Lead Types');
    } else if (overrides.overrideAcceptedLeadTypes.length > 0) {
      summary.push(`Lead Types: ${overrides.overrideAcceptedLeadTypes.join(', ')}`);
    }
    
    if (overrides.overrideLicensedStates.length > 0) {
      summary.push(`States: ${overrides.overrideLicensedStates.join(', ')}`);
    }
    
    if (overrides.ignoreContractCaps) {
      summary.push('Ignore Caps');
    }
    
    if (overrides.ignorePricingRules) {
      summary.push('Ignore Pricing');
    }
    
    if (summary.length > 0) {
      result.push({ agentId, summary });
    }
  }
  
  return result;
}

/**
 * Get count of agents with active overrides.
 */
export function getOverriddenAgentCount(): number {
  return PER_AGENT_OVERRIDE_STORE.size;
}
